import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { AudioSourceType } from '../../../models/stt-chunked.model';
import {
  DisplayMediaErrorMessages,
  MicrophoneErrorMessages,
  MESSAGES,
} from '../../../util/constants';
import { CVANotificationService } from '../../cva-notification/cva-notification.service';
import { NotificationClassEnum } from '../../../models/notification-class.enum';
import { AudioDeviceService } from '../audio-device/audio-device.service';

@Injectable({
  providedIn: 'root'
})
export class AudioStreamService implements OnDestroy {
  private streamSubject = new BehaviorSubject<MediaStream | null>(null);
  stream$ = this.streamSubject.asObservable();

  /**
   * Always requests the browser's default microphone. No deviceId
   * constraint — getUserMedia resolves whichever mic the OS/browser
   * currently treats as default.
   */

  private _audioSource: AudioSourceType = 'transcribe';
  private _teleHealthStream: MediaStream | null = null;
  private _teleHealthAudioStream: MediaStream | null = null;
  private _microphoneStream: MediaStream | null = null;
  private _mixContext: AudioContext | null = null;
  private _isCleaningUp = false;

  private readonly _screenShareEnded$ = new Subject<void>();
  readonly screenShareEnded$ = this._screenShareEnded$.asObservable();

  constructor(
    private readonly audioDeviceService: AudioDeviceService,
    private readonly cvaNotificationService: CVANotificationService,
  ) { }

  get audioSource(): AudioSourceType {
    return this._audioSource;
  }

  get current(): MediaStream | null {
    return this.streamSubject.value;
  }

  get teleHealthStream(): MediaStream | null {
    return this._teleHealthStream;
  }

  get teleHealthAudioStream(): MediaStream | null {
    return this._teleHealthAudioStream;
  }

  get hasActiveTelehealthAudio(): boolean {
    if (!this._teleHealthAudioStream) return false;
    const audioTracks = this._teleHealthAudioStream.getAudioTracks();
    return audioTracks.length > 0 && audioTracks[0].readyState === 'live';
  }

  hasStream(): boolean {
    return !!this.streamSubject.value;
  }

  setAudioSource(source: AudioSourceType): void {
    this._audioSource = source;
  }

  // Requests microphone input using the selected device when one is set,
  // otherwise the browser default. Falls back to default if the selected
  // device is unavailable.
  async init(): Promise<MediaStream> {
    try {
      this.cleanupTelehealthStreams();

      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error(MicrophoneErrorMessages.NotSupportedError);
      }

      const stream = await this.getMicrophoneStream();
      const audioTracks = stream.getAudioTracks();
      if (audioTracks.length === 0) {
        this.stopStream(stream);
        throw new Error(MicrophoneErrorMessages.NotFoundError);
      }

      this.streamSubject.next(stream);
      return stream;
    } catch (error) {
      console.error('[AudioStream] getUserMedia failed', error);
      throw new Error(this.getUserMediaErrorMessage(error));
    }
  }

  stop(): void {
    const stream = this.streamSubject.value;
    if (stream) {
      this.stopStream(stream);
      this.streamSubject.next(null);
      console.log('[AudioStream] stream stopped');
    }
    this.cleanupAllStreams();
  }

  async restart(): Promise<MediaStream> {
    this.stop();
    return this.init();
  }

  // Screen share + optional microphone, mixed to a single mono track.
  // Display-media is requested here; the mixed stream is published as `current`.
  async initializeTelehealthMode(): Promise<MediaStream> {
    try {
      if (!navigator.mediaDevices?.getDisplayMedia) {
        throw new Error(DisplayMediaErrorMessages.NotSupportedError);
      }

      const screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: true,
      });

      const screenAudioTracks = screenStream.getAudioTracks();
      if (screenAudioTracks.length === 0) {
        this.stopStream(screenStream);
        throw new Error(DisplayMediaErrorMessages.NoAudioTrack);
      }

      this._teleHealthStream = screenStream;
      this._microphoneStream = await this.tryGetMicrophoneStream();

      const mixedMonoStream = await this.createMixedMonoStream(
        screenStream,
        this._microphoneStream,
      );

      this.setupScreenShareEndHandler(screenStream);
      this._teleHealthAudioStream = mixedMonoStream;
      this._audioSource = 'telehealth';
      this.streamSubject.next(mixedMonoStream);
      return mixedMonoStream;
    } catch (error) {
      this.cleanupAllStreams();
      throw new Error(this.getDisplayMediaErrorMessage(error));
    }
  }

  async updateTelehealthMicrophoneDevice(deviceId: string): Promise<MediaStream | null> {
    try {
      if (this._audioSource !== 'telehealth' || !this._teleHealthStream) {
        throw new Error('Not in active Telehealth mode');
      }

      this.stopStream(this._microphoneStream);
      this._microphoneStream = await this.tryGetMicrophoneStream(deviceId);

      const newMixedStream = await this.createMixedMonoStream(
        this._teleHealthStream,
        this._microphoneStream,
      );

      this.stopStream(this._teleHealthAudioStream);
      this._teleHealthAudioStream = newMixedStream;
      this.streamSubject.next(newMixedStream);
      return newMixedStream;
    } catch (error) {
      this.cvaNotificationService.showNotification(
        'Failed to update microphone device: ' + this.extractErrorMessage(error),
        NotificationClassEnum.ERROR,
      );
      return null;
    }
  }

  reset(): void {
    this.cleanupAllStreams();
    this._audioSource = 'transcribe';
  }

  ngOnDestroy(): void {
    this.cleanupAllStreams();
    this._screenShareEnded$.complete();
    this.streamSubject.complete();
  }

  getUserMediaErrorMessage(error: unknown): string {
    if (error instanceof DOMException) {
      return MicrophoneErrorMessages[
        error.name as keyof typeof MicrophoneErrorMessages
      ] ?? MicrophoneErrorMessages.UnknownError;
    }
    if (error instanceof Error && error.message) {
      const known = Object.values(MicrophoneErrorMessages);
      if (known.includes(error.message as MicrophoneErrorMessages)) {
        return error.message;
      }
    }
    return MicrophoneErrorMessages.UnknownError;
  }

  private async getMicrophoneStream(deviceId?: string): Promise<MediaStream> {
    const preferredId = deviceId || this.audioDeviceService.getDeviceId();
    try {
      return await navigator.mediaDevices.getUserMedia({
        audio: this.audioConstraints(preferredId),
        video: false,
      });
    } catch (error) {
      if (!preferredId) throw error;
      this.cvaNotificationService.showNotification(
        MESSAGES.MIC_DEVICE_UNAVAILABLE,
        NotificationClassEnum.ERROR,
      );
      return navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    }
  }

  private async tryGetMicrophoneStream(deviceId?: string): Promise<MediaStream | null> {
    try {
      return await this.getMicrophoneStream(deviceId);
    } catch (micError) {
      console.warn('Failed to access microphone for Telehealth mode, continuing with screen audio only.', micError);
      return null;
    }
  }

  private audioConstraints(deviceId?: string): boolean | MediaTrackConstraints {
    return deviceId ? { deviceId: { exact: deviceId } } : true;
  }

  private async createMixedMonoStream(
    screenStream: MediaStream,
    micStream?: MediaStream | null,
  ): Promise<MediaStream> {
    if (!this._mixContext || this._mixContext.state === 'closed') {
      this._mixContext = new AudioContext();
    }

    const audioContext = this._mixContext;
    if (audioContext.state === 'suspended') {
      await audioContext.resume();
    }

    const destination = audioContext.createMediaStreamDestination();
    const screenTrack = screenStream.getAudioTracks()[0];
    if (!screenTrack || screenTrack.readyState !== 'live') {
      throw new Error(DisplayMediaErrorMessages.NoAudioTrack);
    }

    const screenSource = audioContext.createMediaStreamSource(new MediaStream([screenTrack]));
    const screenMerger = audioContext.createChannelMerger(1);
    screenSource.connect(screenMerger);
    screenMerger.connect(destination);

    if (micStream) {
      const micTrack = micStream.getAudioTracks()[0];
      if (micTrack && micTrack.readyState === 'live') {
        try {
          const micSource = audioContext.createMediaStreamSource(new MediaStream([micTrack]));
          const micMerger = audioContext.createChannelMerger(1);
          micSource.connect(micMerger);
          micMerger.connect(destination);
        } catch (micError) {
          console.warn('Failed to mix microphone audio, continuing with screen audio only.', micError);
        }
      }
    }

    return destination.stream;
  }

  private setupScreenShareEndHandler(screenStream: MediaStream): void {
    const videoTrack = screenStream.getVideoTracks()[0];
    if (!videoTrack) return;

    const originalOnEnded = videoTrack.onended;
    videoTrack.onended = (event: Event) => {
      if (originalOnEnded) originalOnEnded.call(videoTrack, event);
      this._screenShareEnded$.next();
      this.cleanupAllStreams();
    };
  }

  private stopStream(stream: MediaStream | null): void {
    if (!stream) return;
    for (const track of stream.getTracks()) {
      track.onended = null;
      track.stop();
    }
  }

  private cleanupTelehealthStreams(): void {
    this.stopStream(this._teleHealthStream);
    this._teleHealthStream = null;

    this.stopStream(this._teleHealthAudioStream);
    this._teleHealthAudioStream = null;

    this.stopStream(this._microphoneStream);
    this._microphoneStream = null;
  }

  cleanupAllStreams(): void {
    if (this._isCleaningUp) return;
    this._isCleaningUp = true;

    const published = this.streamSubject.value;
    if (published) {
      this.stopStream(published);
      this.streamSubject.next(null);
    }

    this.cleanupTelehealthStreams();

    if (this._mixContext && this._mixContext.state !== 'closed') {
      this._mixContext.close()
        .catch(() => undefined)
        .finally(() => {
          this._mixContext = null;
          this._isCleaningUp = false;
        });
    } else {
      this._mixContext = null;
      this._isCleaningUp = false;
    }
  }

  private getDisplayMediaErrorMessage(e: unknown): string {
    const errorMessage = this.extractErrorMessage(e);
    if (errorMessage.includes('No audio track') || errorMessage === DisplayMediaErrorMessages.NoAudioTrack) {
      return DisplayMediaErrorMessages.NoAudioTrack;
    }
    if (e instanceof DOMException && e.name in DisplayMediaErrorMessages) {
      return DisplayMediaErrorMessages[e.name as keyof typeof DisplayMediaErrorMessages];
    }
    if (errorMessage && Object.values(DisplayMediaErrorMessages).includes(errorMessage as DisplayMediaErrorMessages)) {
      return errorMessage;
    }
    return DisplayMediaErrorMessages.UnknownError;
  }

  private extractErrorMessage(e: unknown): string {
    if (e instanceof Error) return e.message;
    if (typeof e === 'string') return e;
    return '';
  }
}
